import { useState } from 'react'
import PQueue from 'p-queue'
import _ from 'lodash'
import clx from 'classnames'
import { connect } from 'react-redux'
import { formValueSelector } from 'redux-form'
import { Row, Col } from 'antd'
import {
  Button,
  Icon,
  ImageUploader,
  ProposalSection,
  Tip,
  Toast,
  getBase64Image,
  getComposedImage as getComposedImageHelper
} from '@vzmi/shp_shared_compo'
import { safeAwait, httpHandler } from '@vzmi/component_ec_nodejs_util/dist/lib/FetchDataUtil'
import { delay } from '@vzmi/component_ec_nodejs_util/dist/lib/renderUtil'
import { sendI13nClickEvent } from '../../../../../lib/i13nUtil'
import { UPDATE_LISTING_FORM_KEY } from '../../../../constants/proposalTypes'
import CollapsePanel from '../../../Proposal/UpdateListing/CollapsePanel/CollapsePanel'
import useS3Upload from '../../../../hooks/useS3Upload'
import { GA_CLICK_ACTION, getI13nModel } from '../../BatchApply.ga'
import styles from './index.css'
import {
  getErrors,
  applyUpdate,
  getListingFirstImages,
  getListingsWithComposedImage
} from './helper'

const CUSTOM_IMAGE_RULES = [
  '請上傳透明背景之套框圖，系統將自動組合套框圖與第1張主圖，產生新的合成圖片，並放置於主圖1的位置，原第1張主圖將挪到最後，其他圖片位置不動',
  '請確認賣場圖片數<=9張，否則將無法產生套框合成圖片',
  (
    <span
      key='tip'
      className={styles.composeTip}
    >
      建議搭配預約與回檔功能，屆時活動結束可自動回檔為無套框主圖
    </span>
  ),
  '圖片尺寸：1000x1000 px / 圖片格式：PNG'
]

const queue = new PQueue({ concurrency: 5 })

const ComposeBlock = (props) => {
  const {
    listings = [],
    s3ServiceActions,
    pageActions,
    changeFormListings,
    i13n
  } = props
  const [images, setImages] = useState([])
  const [errors, setErrors] = useState([])
  const { trigger } = useS3Upload(s3ServiceActions)

  const customRequest = ({ onSuccess, onError, file }) => {
    return getBase64Image(file)
      .then(onSuccess)
      .catch(error => onError(error, { file }))
  }

  const onUploadFileChange = async (props) => {
    const { file } = props
    setImages(applyUpdate(images, file))
  }

  const onUploadFileRemove = () => {
    setImages([])
    setErrors([])
  }

  const getBase64Yimg = async (url) => {
    const [error, payload] = await safeAwait(
      s3ServiceActions.fetchBase64Yimg({ url }).then(httpHandler)
    )
    if (error) {
      console.log(error)
      return ['賣場圖片解析失敗']
    }

    return [undefined, payload]
  }

  const getComposedImage = async (base64FrameImage, listingFirstImage) => {
    const [error, base64Yimg] = await getBase64Yimg(listingFirstImage)
    if (error) {
      return [error]
    }

    const result = await getComposedImageHelper(base64Yimg, base64FrameImage, listingFirstImage)
    return result
  }

  const sendClickGA = () => {
    const i13nModel = getI13nModel({
      type: GA_CLICK_ACTION.BATCH_APPLY_COMPOSE_IMAGE
    })
    sendI13nClickEvent({ i13n, i13nModel })
  }

  const onCompose = async () => {
    const file = _.get(images, '0', {})
    const newErrors = getErrors(file, listings)
    setErrors(newErrors)
    if (!_.isEmpty(newErrors)) {
      return
    }

    sendClickGA()
    Toast.info('套框合成圖片生成中')
    pageActions.setIsLoading(true)
    const frameFile = _.get(images, '0.originFileObj', {})
    const base64FrameImage = await getBase64Image(frameFile)
    const listingFirstImages = getListingFirstImages(listings)
    // 將賣場首圖轉換成 base64 並與 frame image 合成新圖
    const composedResults = await queue.addAll(
      listingFirstImages.map(listingFirstImage => () => {
        return getComposedImage(base64FrameImage, listingFirstImage)
      })
    )
    const composedFileError = _.find(composedResults, composedResult => {
      return !_.isEmpty(_.get(composedResult, '0'))
    })
    if (composedFileError) {
      setErrors([composedFileError])
      pageActions.setIsLoading(false)
      Toast.error('套用失敗')
      return
    }

    const composedFiles = _.flow(
      () => _.filter(composedResults, composedResult => {
        return !_.isEmpty(_.get(composedResult, '1'))
      }),
      filteredComposedResults => _.map(filteredComposedResults, (composedResult) => {
        const composedFile = _.get(composedResult, '1.file', {})
        return composedFile
      })
    )()
    // 上傳到全部合成圖到 s3 並取得 yimg url
    const {
      error: uploadError,
      result: composedImageMapByFirstImageUrl
    } = await trigger(composedFiles)
    if (uploadError) {
      setErrors([uploadError])
      Toast.error('套用失敗')
      pageActions.setIsLoading(false)
      return
    }

    // 將全部轉換過合成圖的賣場資料重新寫回頁面狀態
    const listingsWithComposedImage = getListingsWithComposedImage(listings, composedImageMapByFirstImageUrl)
    changeFormListings(listingsWithComposedImage)
    await delay(300)
    pageActions.setIsLoading(false)
    Toast.success('套用成功')
  }

  return (
    <ProposalSection>
      <CollapsePanel
        className={styles.appendBlock}
        panelHeader={(
          <Row
            align='middle'
            className={styles.panelHeader}
          >
            <Col span={4}>
              <span className={styles.iconHolder}>
                <Icon
                  name='compose'
                  color='#e0e4e9'
                  size={15}
                />
              </span>
              <strong className={styles.title}>
                自動套框
              </strong>
            </Col>
          </Row>
        )}
        defaultExpanded={false}
      >
        <Row className={styles.content}>
          <Col span={24}>
            <div className={clx(styles.centerArea, styles.imageUploaderArea)}>
              <ImageUploader
                customRequest={customRequest}
                customImageRules={CUSTOM_IMAGE_RULES}
                handleChange={onUploadFileChange}
                removeFile={onUploadFileRemove}
                verifyUploadImage={file => ({ file })}
                fileList={images}
                hasTagOnImage={false}
                isSingleImageUpload
              />
              <div className={styles.errorArea}>
                {errors.map((error, index) => (
                  <Tip
                    type='important'
                    key={index}
                  >
                    {error}
                  </Tip>
                ))}
              </div>
            </div>
          </Col>
          <Col span={24}>
            <div className={clx(styles.centerArea, styles.composeBtnArea)}>
              <Button
                size='large'
                disabled={_.isEmpty(images)}
                onClick={onCompose}
              >
                確認套用
              </Button>
              <Tip type='info'>
                套用到所有賣場上
              </Tip>
            </div>
          </Col>
        </Row>
      </CollapsePanel>
    </ProposalSection>
  )
}

const selector = formValueSelector(UPDATE_LISTING_FORM_KEY)
const ComposeBlockForm = connect(state => {
  const listings = selector(state, 'listings') || []
  return { listings }
})(ComposeBlock)

export default ComposeBlockForm
